import { Tag, theme } from 'antd';

/**
 * Converts hex color to rgba with specified opacity
 */
const hexToRgba = (hex: string, opacity: number): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

interface CategoryTagProps {
  /** Category name to display */
  name: string;
  /** Category color (hex format) */
  color: string;
  /** Whether the tag can be closed/removed */
  closable?: boolean;
  /** Callback when tag is closed */
  onClose?: () => void;
}

/**
 * CategoryTag Component
 *
 * Displays a category as a colored tag with:
 * - Light background (10% opacity of the color)
 * - Colored text (matching the category color)
 * - Colored border (30% opacity of the color)
 *
 * Used in:
 * - Vendor List (category column)
 * - Vendor Detail (header categories)
 * - Customer List (category column)
 * - Customer Detail (header categories)
 * - Category List (name column)
 * - Filter chips (with closable prop)
 */
const CategoryTag = ({ name, color, closable = false, onClose }: CategoryTagProps) => {
  const { token } = theme.useToken();

  return (
    <Tag
      closable={closable}
      onClose={onClose}
      style={{
        backgroundColor: hexToRgba(color, 0.1),
        color: color,
        border: `1px solid ${hexToRgba(color, 0.3)}`,
        borderRadius: token.borderRadiusSM,
        padding: '2px 8px',
        margin: 0,
      }}
    >
      {name}
    </Tag>
  );
};

export default CategoryTag;
